import { ChevronDown, ChevronRight, Copy } from 'lucide-react';
import { Fragment } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { DELIVERY_KIND_LABELS } from './deliveryKinds';
import { ProactivityExperimentDetailsPanel } from './ProactivityExperimentDetailsPanel';
import {
  useDeleteProactivityExperiment,
  useRevokeProactivityExperiment,
} from './useProactivityExperiments';

import type {
  ProactivityExperimentDetail,
  ProactivityExperimentSummary,
} from './proactivity-experiments-types';
import type { UseQueryResult } from '@tanstack/react-query';

const experimentFlags = (experiment: ProactivityExperimentSummary) => [
  ...(experiment.visible_to_user ? [] : ['Hidden']),
  ...(experiment.billed_to_user ? [] : ['Unbilled']),
  ...(experiment.enroll_new_signups ? ['Enrols signups'] : []),
  ...(experiment.paused_at ? ['Paused'] : []),
];

const recipientCounts = (experiment: ProactivityExperimentSummary) => {
  if (experiment.delivery_kind !== 'card_prompt') {
    return `${experiment.issued_count} active`;
  }
  const dataIssues = experiment.unknown_count
    ? ` · ${experiment.unknown_count} data issue`
    : '';
  return `${experiment.untouched_count} haven't touched it · ${experiment.opened_count} opened · ${experiment.rejected_count} rejected · ${experiment.revoked_count} revoked${dataIssues}`;
};

type ProactivityExperimentsTableProps = {
  experiments: ProactivityExperimentSummary[];
  expandedExperimentId: string | null;
  onExpandedExperimentIdChange: (experimentId: string | null) => void;
  expandedExperimentDetail: UseQueryResult<ProactivityExperimentDetail>;
  onDuplicate: (experiment: ProactivityExperimentSummary) => void;
  onEditCohort: (experiment: ProactivityExperimentSummary) => void;
};

export const ProactivityExperimentsTable = ({
  experiments,
  expandedExperimentId,
  onExpandedExperimentIdChange,
  expandedExperimentDetail,
  onDuplicate,
  onEditCohort,
}: ProactivityExperimentsTableProps) => {
  const revoke = useRevokeProactivityExperiment();
  const remove = useDeleteProactivityExperiment();

  if (experiments.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No proactivity experiments yet.
      </p>
    );
  }

  const revokeExperiment = (experiment: ProactivityExperimentSummary) => {
    if (
      window.confirm(
        `Revoke "${experiment.name}"? Untouched cards will disappear.`
      )
    ) {
      revoke.mutate(experiment.id);
    }
  };

  const deleteExperiment = (experiment: ProactivityExperimentSummary) => {
    if (
      window.confirm(
        `Delete "${experiment.name}"? Remaining cards will be revoked.`
      )
    ) {
      remove.mutate(experiment.id);
    }
  };

  return (
    <>
      {revoke.error || remove.error ? (
        <p role="alert" className="mb-3 text-sm text-destructive">
          {(revoke.error ?? remove.error)?.message}
        </p>
      ) : null}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Kind</TableHead>
            <TableHead>Execution</TableHead>
            <TableHead>Issuance</TableHead>
            <TableHead>Recipients</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {experiments.map((experiment) => {
            const expanded = expandedExperimentId === experiment.id;
            return (
              <Fragment key={experiment.id}>
                <TableRow>
                  <TableCell className="font-medium">
                    {experiment.name}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1">
                      <span>
                        {DELIVERY_KIND_LABELS[experiment.delivery_kind]}
                      </span>
                      {experimentFlags(experiment).map((flag) => (
                        <Badge key={flag} variant="secondary">
                          {flag}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    {experiment.execution.kind === 'mode'
                      ? `Mode: ${experiment.execution.value}`
                      : `Agent: ${experiment.execution.value}`}
                  </TableCell>
                  <TableCell>
                    {experiment.issued_count} issued, {experiment.failed_count}{' '}
                    failed, {experiment.pending_count} pending
                    {experiment.removed_count
                      ? `, ${experiment.removed_count} removed`
                      : ''}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {recipientCounts(experiment)}
                  </TableCell>
                  <TableCell>
                    {new Date(experiment.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        aria-expanded={expanded}
                        onClick={() =>
                          onExpandedExperimentIdChange(
                            expanded ? null : experiment.id
                          )
                        }
                      >
                        {expanded ? <ChevronDown /> : <ChevronRight />}
                        Details
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDuplicate(experiment)}
                      >
                        <Copy />
                        Duplicate
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={
                          experiment.revoke_requested_at !== null ||
                          (revoke.isPending &&
                            revoke.variables === experiment.id)
                        }
                        onClick={() => revokeExperiment(experiment)}
                      >
                        {experiment.revoke_requested_at
                          ? 'Revoke requested'
                          : 'Revoke'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={
                          remove.isPending && remove.variables === experiment.id
                        }
                        onClick={() => deleteExperiment(experiment)}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {expanded ? (
                  <TableRow>
                    <TableCell colSpan={7} className="p-0">
                      <ProactivityExperimentDetailsPanel
                        summary={experiment}
                        detail={expandedExperimentDetail}
                        onEditCohort={() => onEditCohort(experiment)}
                      />
                    </TableCell>
                  </TableRow>
                ) : null}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </>
  );
};
